import React, { useEffect, useState } from "react";
import addYears from "date-fns/esm/addYears";
import format from "date-fns/format";
import getYear from "date-fns/getYear";
import { Route, Switch, matchPath, useLocation, useHistory } from "react-router-dom";

import {
  MarketingActivity as Activity,
  MarketingObjective as Objective,
  FileData,
  marketingApi,
} from "../../api";

import MarketingActivities from "./MarketingActivities";
import MarketingObjectives from "./MarketingObjectives";
import DetailsModal, { ModalItemType } from "./modals/DetailsModal";
import Layout from "../../components/Layout/Layout";

interface IdentifiedItem {
  id: string;
}

interface Params {
  area?: string;
  year?: string;
}

const getDictionary = <T extends IdentifiedItem>(activities: T[]) => {
  const dict: Record<string, T> = {};
  activities.forEach(x => {
    dict[x.id] = x;
  });
  return dict;
};

const Marketing = () => {

  const thisYear = getYear(new Date());
  const minYear = thisYear - 1;
  const maxYear = thisYear + 1;

  const parseYear = (value?: string): number => {
    const y = parseInt(value ?? "");
    if (isNaN(y)) {
      return thisYear;
    }
    return Math.min(Math.max(y, minYear), maxYear);
  };

  const history = useHistory();
  const location = useLocation();

  const match = matchPath<Params>(location.pathname, {
    path: "/marketing/:area?/:year?"
  });

  const [year, setYear] = useState<number>(() => parseYear(match?.params.year));
  const [activitiesById, setActivitiesById] = useState<Record<string, Activity>>({});
  const [objectivesById, setObjectivesById] = useState<Record<string, Objective>>({});
  const [activities, setActivities] = useState<Activity[]>();
  const [objectives, setObjectives] = useState<Objective[]>();
  const [file, setFile] = useState<FileData>();
  const [isLoading, setIsLoading] = useState(true);
  const [itemId, setItemId] = useState<string>();
  const [itemType, setItemType] = useState<ModalItemType>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleSelectActivity = (activityId: string) => {
    setItemId(activityId);
    setItemType("activity");
    setIsModalOpen(true);
  }

  const handleSelectObjective = (objectiveId: string) => {
    setItemId(objectiveId);
    setItemType("objective");
    setIsModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setItemType(void 0);
    setItemId(void 0);
  }

  const fetchActivities = (ids: string[]) =>
    marketingApi.getMarketingActivitiesById({ activityIds: ids })
      .then(response => response.data)
      .catch<Activity[]>(() => []);

  const fetchObjectives = (ids: string[]) =>
    marketingApi.getMarketingObjectivesById({ objectiveIds: ids })
      .then(response => response.data)
      .catch<Objective[]>(() => []);

  const fetch = (year: number) => {
    setIsLoading(true);
    const d = new Date(year, 0);
    const lowerBound = format(d, "yyyy-MM-dd");
    const upperBound = format(addYears(d, 1), "yyyy-MM-dd");
    Promise.all([
      // Activities
      marketingApi.getMarketingActivitiesByDateRange({
        lowerBound,
        upperBound,
      })
        .then(response => response.data)
        .catch<Activity[]>(() => []),
      // Objectives
      marketingApi.getMarketingObjectivesByDateRange({
        lowerBound,
        upperBound,
      })
        .then(response => response.data)
        .catch<Objective[]>(() => []),
      // File
      marketingApi.getMarketingScheduleFile({
        year,
      })
        .then(response => response.data)
        .catch(() => void 0)
    ]).then(([activities, objectives, file]) => {
      const activitiesById = getDictionary(activities);
      const objectivesById = getDictionary(objectives);
      const missingActivitiesById: Record<string, any> = {};
      const missingObjectivesById: Record<string, any> = {};
      objectives.forEach(x => {
        x.activityIds?.forEach(xx => {
          if (!(xx in activitiesById)) {
            missingActivitiesById[xx] = void 0;
          }
        });
      });
      activities.forEach(x => {
        x.objectiveIds?.forEach(xx => {
          if (!(xx in objectivesById)) {
            missingObjectivesById[xx] = void 0;
          }
        });
      });
      const missingActivityIds = Object.keys(missingActivitiesById);
      const missingObjectiveIds = Object.keys(missingObjectivesById);
      Promise.all([
        // Activities
        new Promise<Activity[]>(resolve => {
          if (missingActivityIds.length) {
            fetchActivities(missingActivityIds).then(data => resolve(data));
          }
          else {
            resolve([]);
          }
        }),
        // Objectives
        new Promise<Objective[]>(resolve => {
          if (missingObjectiveIds.length) {
            fetchObjectives(missingObjectiveIds).then(data => resolve(data));
          }
          else {
            resolve([]);
          }
        })
      ]).then(([linkedActivities, linkedObjectives]) => {
        linkedActivities.forEach(x => {
          activitiesById[x.id] = x;
        });
        linkedObjectives.forEach(x => {
          objectivesById[x.id] = x;
        });
        setActivities(activities);
        setObjectives(objectives);
        setActivitiesById(activitiesById);
        setObjectivesById(objectivesById);
        setFile(file);
        setIsLoading(false);
      });
    });
  };

  useEffect(() => {
    const area = match?.params.area;
    if (!area) {
      history.replace(`/marketing/activities`);
    }
  }, [match?.params.area]);

  useEffect(() => {
    const area = match?.params.area;
    const year = match?.params.year;
    if (!!year) {
      let y = parseInt(year);
      if (isNaN(y)) {
        history.replace(`/marketing/${area}`);
      }
      else {
        y = Math.min(Math.max(y, minYear), maxYear);
        history.replace(`/marketing/${area}/${y}`);
      }
    }
  }, [match?.params.year]);

  useEffect(() => {
    setYear(parseYear(match?.params.year));
  }, [match]);

  useEffect(() => {
    if (year !== void 0) {
      fetch(year);
    }
  }, [year]);

  return (
    <>
      <Layout.FullPage>
        <Switch>
          <Route
            key="activities"
            path="/marketing/activities/:year?"
            render={() => <MarketingActivities
              activities={activities}
              file={file}
              year={year}
              isLoading={isLoading}
              handleSelect={handleSelectActivity}
            />}
          />
          <Route
            key="objectives"
            path="/marketing/objectives/:year?"
            render={() => <MarketingObjectives
              objectives={objectives}
              year={year}
              isLoading={isLoading}
              handleSelect={handleSelectObjective}
            />}
          />
        </Switch>
      </Layout.FullPage>

      {isModalOpen &&
        <DetailsModal
          isOpen
          itemId={itemId}
          itemType={itemType}
          activitiesById={activitiesById}
          objectivesById={objectivesById}
          handleSelectActivity={handleSelectActivity}
          handleSelectObjective={handleSelectObjective}
          handleClose={handleCloseModal} />
      }
    </>
  );
};

export default Marketing;
